import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import avo from 'analytics/avo';
import Prefetcher from 'components/essentials/Prefetcher';
import IconHeart2 from 'components/icons/iconHeart2';
import IconHeart4 from 'components/icons/iconHeart4';
import useFabricFavs from 'data-hooks/useFabricFavs';
import IconStarFull from 'components/icons/iconStarFull';
import JBImage from 'components/essentials/JBImage';
import SidebarModal from 'components/consumer/SidebarModal';
import MoveItButton from 'components/consumer/MoveItButton';
import FabricAdditionalAttributes from 'components/consumer/FabricAdditionalAttributes';
import PDPFreeSwatchKitButton from 'components/consumer/PDP/PDPFreeSwatchKitButton';
import PDPOrderSwatchesSidebar from 'components/consumer/PDP/PDPOrderSwatchesSidebar';
import useScreen from 'hooks/useScreen';
import IconClose from 'components/icons/iconClose';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import useIsBrowser from 'hooks/useIsBrowser';
import SanitizedHtml from 'components/consumer/SanitizedHtml';
import { PANTONE_FABRIC_CONFIG } from 'commons/constants';
import PDPFabricReviews from './PDPFabricReviews';
import FabricGallery from '../../GridWithSidebar/components/FabricGallery';

const { pantoneFilterSlug } = PANTONE_FABRIC_CONFIG;

const PDPFabricDetailSidebarUI = ({
  isOpen,
  mode = 'pdp',
  onClose,
  rating,
  reviews,
  selectedFabric,
  galleryType,
  mobileHideHeader = false,
}) => {
  const history = useHistory();
  const [showAttr, setShowAttr] = useState(false);
  const { isMobile, isMobileOrTablet } = useScreen();
  const showNotificationBannerRef = useRef(null);

  const [isOrderSwatchesSidebarOpen, setIsOrderSwatchesSidebarOpen] = useState(
    false
  );

  const onCloseClick = useCallback(() => {
    if (isMobile) history.goBack();
    onClose();
  }, [history, isMobile, onClose]);

  const {
    actions: { setOptionAsFav, removeOptionAsFav } = {},
    data: { userFabricFavs: favFabricsObj } = {},
  } = useFabricFavs();

  const isBrowser = useIsBrowser();

  const location = useMemo(() => {
    if (!isBrowser) {
      return 'PDP';
    }

    const pathname = window?.location?.pathname?.toLowerCase();

    if (pathname?.includes('safeguard-fabrics')) {
      return 'Safeguard Fabrics Page';
    }

    if (pathname?.includes('pet-friendly')) {
      return 'Pet Friendly Page';
    }

    if (pathname?.includes('swatches')) {
      return 'Swatches';
    }

    return 'PDP';
  }, [isBrowser]);

  const favProps = useMemo(
    () => ({
      fabricId: selectedFabric?.option_value_id,
      fabricName: selectedFabric?.option_value,
      fabricColor: selectedFabric?.option_color,
      location,
    }),
    [location, selectedFabric]
  );

  const isFavorited = !!favFabricsObj?.[selectedFabric?.option_value_id];

  const toggleFavClick = favParams => {
    if (favFabricsObj?.[favParams.fabricId] && removeOptionAsFav) {
      removeOptionAsFav(favParams.fabricId);
      avo.fabricUnfavorited(favParams);
    } else if (!favFabricsObj?.[favParams.fabricId] && setOptionAsFav) {
      setOptionAsFav(favParams.fabricId);
      avo.fabricFavorited(favParams);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // wait for sidebar animation
      setTimeout(() => setShowAttr(true), 600);
    } else {
      setShowAttr(false);
    }
  }, [isOpen]);

  const imgixProps = useMemo(
    () => ({
      height: 300,
      width: !isMobile ? 600 : undefined,
      className: isMobile ? 'h-full' : '',
    }),
    [isMobile]
  );

  const onRenderAssets = useMemo(() => {
    if (selectedFabric?.fabricURL || selectedFabric?.fabricFallbackURL) {
      return [
        {
          url: selectedFabric?.fabricURL || selectedFabric?.fabricFallbackURL,
          imgixProps,
        },
      ];
    }

    return [];
  }, [selectedFabric, imgixProps]);

  if (!selectedFabric) return null;

  return (
    <>
      <SidebarModal
        isOpen={isOpen}
        onClose={onClose}
        mobileNoPadding
        desktopNoPadding
        desktopNoCloseIcon
        desktopWidthSize="lg"
        mobileHideHeader={mobileHideHeader}
        mobileIsFullHeight
      >
        <div
          className={classNames('relative bg-white', {
            '-top-5': !isMobileOrTablet && selectedFabric?.sliderImages,
          })}
        >
          <div className="h-[300px]">
            {selectedFabric?.sliderImages &&
            galleryType === 'FabricMultiImg' ? (
              <FabricGallery
                slides={selectedFabric.sliderImages}
                isMobileMaterialSwatches
                topRightSide={
                  <button
                    type="button"
                    className={classNames(
                      'absolute top-7 right-4 mr-[calc(9%_-_2px)] !border-0 z-[1] items-center bg-white border-solid border-gray-light6 rounded-[48px] cursor-pointer flex h-10 justify-center w-10'
                    )}
                    onClick={() => onCloseClick()}
                    aria-label="Close fabric detail sidebar"
                  >
                    <IconClose />
                  </button>
                }
                bottomRightSide={
                  <button
                    type="button"
                    className={classNames(
                      '![background:none] !border-0 !rounded-full !flex !min-h-[34px] !min-w-[34px] !mr-0',
                      'focus:!outline-0',
                      'focus-visible:outline-offset-8',
                      'hover:cursor-pointer',
                      '[&_svg]:!h-8 [&_svg]:!w-8',
                      '[&_svg_path]:!fill-white',
                      '[&:hover_svg_path]:stroke-[#107c8c]',
                      {
                        '[&:hover_svg_path]:!fill-brand': !isFavorited,
                      }
                    )}
                    onClick={e => {
                      e.stopPropagation();

                      toggleFavClick(favProps);
                      if (!isFavorited) {
                        showNotificationBannerRef.current = true;
                      }
                    }}
                  >
                    {isFavorited ? (
                      <IconHeart2 fill="white" color="white" />
                    ) : (
                      <IconHeart4 />
                    )}
                  </button>
                }
              />
            ) : (
              <div className="h-full">
                <Prefetcher onRenderAssets={onRenderAssets}>
                  <JBImage
                    src={selectedFabric.fabricURL}
                    fallbackSrc={selectedFabric.fabricFallbackURL}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...imgixProps}
                  />
                  <div className="absolute top-64 right-[5%] w-fit">
                    <button
                      type="button"
                      className={classNames(
                        '![background:none] !border-0 !rounded-full !flex !min-h-[34px] !min-w-[34px] !mr-0',
                        'focus:!outline-0',
                        'focus-visible:outline-offset-8',
                        'hover:cursor-pointer',
                        '[&_svg]:!h-8 [&_svg]:!w-8',
                        '[&_svg_path]:!fill-white',
                        '[&:hover_svg_path]:stroke-[#107c8c]',
                        {
                          'relative bottom-5': isMobile,
                          '[&:hover_svg_path]:!fill-brand': !isFavorited,
                        }
                      )}
                      onClick={e => {
                        e.stopPropagation();

                        toggleFavClick(favProps);
                        if (!isFavorited) {
                          showNotificationBannerRef.current = true;
                        }
                      }}
                    >
                      {isFavorited ? (
                        <IconHeart2 fill="white" color="white" />
                      ) : (
                        <IconHeart4 />
                      )}
                    </button>
                  </div>
                </Prefetcher>
              </div>
            )}
          </div>

          {galleryType === 'FabricSingleImg' && (
            <MoveItButton
              className="absolute top-6 right-[calc(1%_-_5px)] mr-[calc(4%_-_2px)] !bg-white !border-0"
              onClick={onCloseClick}
            />
          )}

          <div className="px-8 py-10 max-[767px]:p-4">
            <h2 className="text-2xl font-bold m-0">
              {selectedFabric.option_value}
              {selectedFabric.exclude_from_sale ? ' - Limited Edition' : ''}
            </h2>

            {reviews?.length > 0 && (
              <div className="flex items-center [&_span]:flex [&_span]:items-center my-2 mx-0">
                <span>
                  <IconStarFull className="relative -top-px w-[11px] h-[11px] max-w-[inherit] mr-[.2rem] [&_path]:fill-yellow [&_path]:stroke-[none]" />
                </span>
                {rating > 0 && (
                  <>
                    <span className="text-xs">{rating}/5</span>&nbsp;
                  </>
                )}
                <span className="text-xs">
                  ({reviews.length} review{reviews.length > 1 ? 's' : ''})
                </span>
              </div>
            )}

            {showAttr && (
              <FabricAdditionalAttributes
                selectedItemGroupMaterial={selectedFabric.group_material}
                selectedItemGroupCleaningCode={
                  selectedFabric.group_cleaning_code
                }
                isPetAndKidFriendly={!!selectedFabric.pet_friendly}
                isSustainable={!!selectedFabric.sustainable_fabric}
                isGreenguardGold={!!selectedFabric.greenguard_fabric}
                selectedItemTier={selectedFabric.option_value_tier}
                isPantoneFabric={selectedFabric[pantoneFilterSlug]}
              />
            )}

            <SanitizedHtml
              className="mx-0 my-6 text-sm"
              html={selectedFabric.option_description}
            />

            {mode !== 'quickview' && (
              <div className="mb-12">
                <PDPFreeSwatchKitButton
                  onClick={() => {
                    setIsOrderSwatchesSidebarOpen(true);
                    onClose();
                  }}
                />
              </div>
            )}

            <PDPFabricReviews reviews={reviews} />
          </div>
        </div>
      </SidebarModal>

      <PDPOrderSwatchesSidebar
        show={isOrderSwatchesSidebarOpen}
        onClose={() => {
          setIsOrderSwatchesSidebarOpen(false);
        }}
        closeSlidePanel={() => {
          setIsOrderSwatchesSidebarOpen(false);
        }}
      />
    </>
  );
};

PDPFabricDetailSidebarUI.propTypes = {
  isOpen: PropTypes.bool,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  onClose: PropTypes.func,
  rating: PropTypes.number,
  reviews: PropTypes.array,
  galleryType: PropTypes.string,
  selectedFabric: PropTypes.shape({
    option_value: PropTypes.string,
    option_description: PropTypes.string,
    group_material: PropTypes.string,
    group_cleaning_code: PropTypes.string,
    pet_friendly: PropTypes.number,
    exclude_from_sale: PropTypes.bool,
    sustainable_fabric: PropTypes.number,
    option_value_tier: PropTypes.number,
    fabricURL: PropTypes.string,
    fabricFallbackURL: PropTypes.string,
    sliderImages: PropTypes.array,
    option_value_id: PropTypes.number,
    option_color: PropTypes.string,
    location: PropTypes.string,
    greenguard_fabric: PropTypes.bool,
  }),
  mobileHideHeader: PropTypes.bool,
};

export default withBrowserOnly(PDPFabricDetailSidebarUI);
